import { User, Smartphone, Calendar, ArrowRight, ArrowLeft, ArrowLeftRight, Shirt, Clock, Pencil, ZoomIn } from 'lucide-react';
import { formatCurrency, toProperCase } from '../utils/helpers';
import type { Player, Team, AppUser } from '../types';

interface PlayerCardProps {
  player: Player;
  teams: Team[];
  currentUser: AppUser;
  onManagePlayer: (player: Player) => void;
  onEditPlayer?: (player: Player) => void;
  onPhotoClick?: (player: Player) => void;
}

// Month abbreviations for DD-mmm-YYYY format
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// Format DOB as DD-mmm-YYYY (e.g., 13-Oct-1987)
const formatDOB = (dob: string | undefined): string => {
  if (!dob) return '-';
  try {
    let date: Date;
    if (dob.includes('-') && dob.split('-')[0].length === 4) {
      // Format: YYYY-MM-DD (API format)
      date = new Date(dob);
    } else if (dob.includes('/')) {
      // Format: DD/MM/YYYY (legacy format)
      const parts = dob.split('/');
      if (parts.length !== 3) return dob;
      date = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    } else {
      return dob;
    }
    if (isNaN(date.getTime())) return dob;
    const day = date.getDate().toString().padStart(2, '0');
    const month = MONTHS[date.getMonth()];
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  } catch {
    return dob;
  }
};

// Calculate age from date string (supports YYYY-MM-DD and DD/MM/YYYY formats)
const calculateAge = (dob: string | undefined): number | null => {
  if (!dob) return null;
  try {
    let birthDate: Date;

    if (dob.includes('-') && dob.split('-')[0].length === 4) {
      // Format: YYYY-MM-DD (API format)
      birthDate = new Date(dob);
    } else if (dob.includes('/')) {
      // Format: DD/MM/YYYY (legacy format)
      const parts = dob.split('/');
      if (parts.length !== 3) return null;
      birthDate = new Date(parseInt(parts[2]), parseInt(parts[1]) - 1, parseInt(parts[0]));
    } else {
      return null;
    }

    if (isNaN(birthDate.getTime())) return null;

    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) age--;
    return age;
  } catch {
    return null;
  }
};

// Convert UTC datetime to IST and format as DD-mmm-YYYY hh:mm AM/PM
// Input format: "YYYY-MM-DD HH:mm:ss" (e.g., "2025-12-01 10:42:38")
const formatDateTimeIST = (utcDatetime: string | undefined): string => {
  if (!utcDatetime) return '-';
  try {
    // Parse "YYYY-MM-DD HH:mm:ss" format by replacing space with 'T' and adding 'Z' for UTC
    const isoString = utcDatetime.replace(' ', 'T') + 'Z';
    const date = new Date(isoString);
    if (isNaN(date.getTime())) return '-';

    // Convert to IST (UTC + 5:30)
    const istDate = new Date(date.getTime() + (5.5 * 60 * 60 * 1000));

    const day = istDate.getUTCDate().toString().padStart(2, '0');
    const month = MONTHS[istDate.getUTCMonth()];
    const year = istDate.getUTCFullYear();

    let hours = istDate.getUTCHours();
    const minutes = istDate.getUTCMinutes().toString().padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // 0 should be 12

    return `${day}-${month}-${year} ${hours}:${minutes} ${ampm}`;
  } catch {
    return '-';
  }
};

// Get side icon
const SideIcon = ({ side }: { side: string | undefined }) => {
  const s = side?.toLowerCase();
  if (s === 'right') return <ArrowRight className="w-3 h-3 mr-1" />;
  if (s === 'left') return <ArrowLeft className="w-3 h-3 mr-1" />;
  if (s === 'both') return <ArrowLeftRight className="w-3 h-3 mr-1" />;
  return null;
};

const PlayerCard = ({ player, teams, currentUser, onManagePlayer, onEditPlayer, onPhotoClick }: PlayerCardProps) => {
  const team = teams.find(t => t.id === player.teamId);
  // Support both API field names (player_dob, jersey_size) and legacy names (date_of_birth, tshirt_size)
  const dob = player.details?.player_dob || player.details?.date_of_birth;
  const jerseySize = player.details?.jersey_size || player.details?.tshirt_size;
  const age = calculateAge(dob);
  const jerseyName = player.details?.jersey_name || player.name.split(' ')[0];
  const hasPhoto = !!player.details?.photo_url;

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 overflow-hidden hover:shadow-lg transition-all flex flex-col">
      {/* Blue Gradient Header */}
      <div className="h-28 bg-gradient-to-br from-indigo-500 via-indigo-600 to-blue-600 relative">
        {/* Status Badge */}
        {player.status === 'sold' && (
          <div className="absolute top-3 right-3 px-2 py-1 rounded bg-green-500 text-white text-xs font-bold shadow">
            SOLD
          </div>
        )}

        {/* Edit Button (Admin only) */}
        {currentUser.role === 'admin' && onEditPlayer && (
          <button
            onClick={() => onEditPlayer(player)}
            className="absolute top-3 left-3 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            title="Edit Player"
          >
            <Pencil className="w-4 h-4 text-white" />
          </button>
        )}

        {/* Profile Image */}
        <div className="absolute -bottom-8 left-4">
          <div
            className={`w-20 h-20 rounded-full border-4 border-white bg-slate-100 overflow-hidden shadow-lg relative group ${hasPhoto ? 'cursor-pointer' : ''}`}
            onClick={() => hasPhoto && onPhotoClick && onPhotoClick(player)}
          >
            {hasPhoto ? (
              <>
                <img src={player.details!.photo_url} alt={player.name} className="w-full h-full object-cover" />
                {/* Zoom overlay on hover */}
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white" />
                </div>
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300">
                <User className="w-10 h-10 text-slate-400" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="pt-10 px-4 pb-4 flex-1 flex flex-col">
        {/* Name & Phone */}
        <h3 className="font-bold text-lg text-slate-800">{toProperCase(player.name)}</h3>
        <div className="flex items-center text-sm text-slate-500 mt-1">
          <Smartphone className="w-3.5 h-3.5 mr-1.5" />
          <span>{player.mobile_number}</span>
        </div>

        {/* Gender & Side Row */}
        <div className="grid grid-cols-2 gap-3 mt-4">
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Gender</p>
            <p className="font-semibold text-slate-700 mt-0.5">{player.details?.player_gender || '-'}</p>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <p className="text-xs text-slate-400 uppercase tracking-wide">Playing Side</p>
            <p className="font-semibold text-slate-700 mt-0.5 flex items-center">
              <SideIcon side={player.details?.playing_side} />
              {player.details?.playing_side || '-'}
            </p>
          </div>
        </div>

        {/* DOB Row */}
        <div className="bg-slate-50 rounded-lg p-3 mt-3">
          <p className="text-xs text-slate-400 uppercase tracking-wide">DOB</p>
          <p className="font-semibold text-slate-700 mt-0.5 flex items-center">
            <Calendar className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            {formatDOB(dob)}
            {age && <span className="text-slate-500 font-normal ml-1">({age} yrs)</span>}
          </p>
        </div>

        {/* Registered Date/Time Row */}
        <div className="bg-slate-50 rounded-lg p-3 mt-3">
          <p className="text-xs text-slate-400 uppercase tracking-wide">Registered On</p>
          <p className="font-semibold text-slate-700 mt-0.5 flex items-center">
            <Clock className="w-3.5 h-3.5 mr-1.5 text-slate-400" />
            {formatDateTimeIST(player.createdDatetime)}
          </p>
        </div>

        {/* Sold Info */}
        {player.status === 'sold' && team && (
          <div className="mt-3 bg-green-50 p-3 rounded-lg border border-green-200">
            <p className="text-xs text-green-600 font-medium uppercase">Sold To</p>
            <p className="font-bold text-green-800 truncate">{team.name}</p>
            <p className="text-green-700 font-mono font-bold">{formatCurrency(player.soldPrice)} pts</p>
          </div>
        )}

        {/* Footer - Jersey & Size */}
        <div className="mt-auto pt-4 border-t border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Shirt className="w-4 h-4 text-slate-400" />
            <span className="font-semibold text-slate-700">{toProperCase(jerseyName)}</span>
            <span className="bg-slate-200 text-slate-600 px-2 py-0.5 rounded text-sm font-bold">
              #{player.details?.jersey_number || '00'}
            </span>
          </div>
          <span className="text-sm font-medium text-indigo-600 bg-indigo-50 px-2 py-1 rounded">
            Size: {jerseySize || '-'}
          </span>
        </div>

        {/* Admin Controls */}
        {currentUser.role === 'admin' && (
          <div className="mt-4">
            <button
              onClick={() => onManagePlayer(player)}
              className={`w-full py-2.5 rounded-xl text-sm font-bold transition-colors ${
                player.status === 'sold'
                  ? 'bg-slate-100 text-slate-600 hover:bg-red-50 hover:text-red-600 border border-slate-200'
                  : 'bg-indigo-600 text-white hover:bg-indigo-700 shadow-md shadow-indigo-200'
              }`}
            >
              {player.status === 'sold' ? 'Manage / Remove' : 'Start Bidding / Tag'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default PlayerCard;

